import { navigate } from "gatsby";
import React, { useEffect, useRef, useState } from "react";
import {
  FacebookShareButton,
  LinkedinShareButton,
  TwitterShareButton,
} from "react-share";
import { FaFacebookF, FaLinkedinIn, FaTwitter } from "react-icons/fa";
import Layout from "../../components/layouts/layout";
import DataService from "../../services/api/data_services";
import { BASE_SITE_URL } from "../../shared/constants";
import ContactUsRightGrid from "../../templates/ContactUsRightGrid";
import "../../components/whitePaperBlog/index.scss";
import "../../components/skeletonLoader/index.scss";

const WhitePaper = ({ pageContext: { whitepaper } }) => {
  const [whitePaperContent, setWhitePaperContent] = useState({});
  const formRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const queryString =
    typeof window !== "undefined" ? window.location.href.split("/")[4] : "";

  useEffect(() => {
    DataService.getData(`/admin/api/post-details?slug=${queryString}`)
      .then((res) => {
        setWhitePaperContent(res.data);
        setIsLoading(false);
      })
      .catch(() => navigate(-1));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryString]);

  const articleUrl = `${BASE_SITE_URL}/white-paper/${
    whitePaperContent?.slug ?? ""
  }`;

  const hasForm = whitePaperContent?.show_contact_form === 1;
  const hasPdf =
    whitePaperContent?.pdf_file && whitePaperContent.pdf_file !== "NULL";
  const hasImage =
    whitePaperContent?.mega_view_image &&
    whitePaperContent.mega_view_image !== "NULL";

  const keyPoints = [
    {
      title: whitePaperContent?.key_points_big,
      desc: whitePaperContent?.key_points,
    },
    {
      title: whitePaperContent?.key_points_big1,
      desc: whitePaperContent?.key_points1,
    },
    {
      title: whitePaperContent?.key_points_big2,
      desc: whitePaperContent?.key_points2,
    },
  ].filter((kp) => kp.title);

  const authorInitials = whitePaperContent?.author_name
    ? whitePaperContent.author_name
        .split(" ")
        .map((n) => n[0])
        .join("")
        .slice(0, 2)
    : "";

  const scrollToForm = (e) => {
    e.preventDefault();
    if (formRef.current) {
      const top =
        formRef.current.getBoundingClientRect().top + window.scrollY - 88;
      window.scrollTo({ top, behavior: "smooth" });
    }
  };

  const hasBody =
    whitePaperContent?.primary_description_1 ||
    whitePaperContent?.primary_description_2 ||
    whitePaperContent?.primary_description_3 ||
    keyPoints.length > 0 ||
    whitePaperContent?.video_url ||
    whitePaperContent?.testimonial?.length > 0;

  return (
    <Layout
      seo={{
        title: whitePaperContent?.og_title || whitepaper?.og_title,
        ogtitle: whitepaper?.og_title,
        description: whitepaper
          ? whitepaper?.og_description
          : whitePaperContent?.og_description,
        ogurl: `${BASE_SITE_URL}/white-paper/${whitepaper?.slug}/`,
        ogimage: whitepaper?.og_image,
      }}
    >
      {isLoading ? (
        <>
          {/* White paper hero skeleton */}
          <div className="wph-skl">
            <div className="wph-skl__inner">
              <div className="wph-skl__left">
                <div className="skl skl--dark wph-skl__badge" />
                <div className="skl skl--dark wph-skl__title-1" />
                <div className="skl skl--dark wph-skl__title-2" />
                <div className="wph-skl__meta">
                  <div className="skl skl--dark wph-skl__avatar" />
                  <div className="wph-skl__meta-lines">
                    <div className="skl skl--dark wph-skl__meta-a" />
                    <div className="skl skl--dark wph-skl__meta-b" />
                  </div>
                </div>
              </div>
              <div className="wph-skl__right">
                <div className="skl skl--dark wph-skl__card-img" />
                <div className="wph-skl__card-footer">
                  <div className="skl skl--dark wph-skl__card-line-a" />
                  <div className="skl skl--dark wph-skl__card-line-b" />
                  <div className="skl skl--dark wph-skl__card-btn" />
                </div>
              </div>
            </div>
          </div>
          {/* White paper body skeleton */}
          <div className="wp-body-skl">
            <div className="skl wp-body-skl__heading" />
            <div className="wp-body-skl__lines">
              {[
                "full",
                "lg",
                "full",
                "md",
                "full",
                "lg",
                "sm",
                "full",
                "lg",
                "full",
                "md",
              ].map((s, i) => (
                <div
                  key={i}
                  className={`skl wp-body-skl__line wp-body-skl__line--${s}`}
                />
              ))}
            </div>
          </div>
        </>
      ) : (
        <>
          {/* ============ 1. HERO ============ */}
          {whitePaperContent?.title && (
            <section className="wph">
              <div className="wph__inner body-container">
                <div className="wph__split">
                  {/* LEFT: text */}
                  <div className="wph__left">
                    <span className="wph__badge">White Paper</span>
                    <h1 className="wph__title">{whitePaperContent.title}</h1>
                    {whitePaperContent?.banner_image_title &&
                      whitePaperContent.banner_image_title !==
                        whitePaperContent.title && (
                        <p className="wph__tagline">
                          {whitePaperContent.banner_image_title}
                        </p>
                      )}
                    {whitePaperContent?.author_name && (
                      <div className="wph__meta">
                        {whitePaperContent?.author_image ? (
                          <img
                            src={whitePaperContent.author_image}
                            alt={whitePaperContent.author_name}
                            className="wph__avatar"
                          />
                        ) : (
                          <div className="wph__initials">{authorInitials}</div>
                        )}
                        <div className="wph__meta-info">
                          <span className="wph__meta-name">
                            {whitePaperContent.author_name}
                          </span>
                          {whitePaperContent?.position && (
                            <span className="wph__meta-pos">
                              {whitePaperContent.position}
                            </span>
                          )}
                          {whitePaperContent?.published_date && (
                            <span className="wph__meta-date">
                              {whitePaperContent.published_date}
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* RIGHT: document card */}
                  <div className="wph__right">
                    <div className="wph__doc-card">
                      {hasImage ? (
                        <img
                          src={whitePaperContent.mega_view_image}
                          alt={whitePaperContent.title}
                          className="wph__doc-cover"
                        />
                      ) : (
                        <div className="wph__doc-placeholder">
                          <svg
                            width="40"
                            height="40"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            aria-hidden="true"
                          >
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                            <polyline points="14 2 14 8 20 8" />
                            <line x1="16" y1="13" x2="8" y2="13" />
                            <line x1="16" y1="17" x2="8" y2="17" />
                          </svg>
                          <span>White Paper</span>
                        </div>
                      )}
                      <div className="wph__doc-footer">
                        <p className="wph__doc-label">White Paper</p>
                        <p className="wph__doc-title">
                          {whitePaperContent.banner_image_title ||
                            whitePaperContent.title}
                        </p>
                        {hasForm ? (
                          <button
                            onClick={scrollToForm}
                            className="wph__doc-btn"
                            aria-label="Get free access to this white paper"
                          >
                            <svg
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2.2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              aria-hidden="true"
                            >
                              <rect
                                x="3"
                                y="11"
                                width="18"
                                height="11"
                                rx="2"
                                ry="2"
                              />
                              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                            </svg>
                            Get Free Access
                          </button>
                        ) : hasPdf ? (
                          <a
                            href={whitePaperContent.pdf_file}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="wph__doc-btn"
                          >
                            <svg
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2.2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              aria-hidden="true"
                            >
                              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                              <polyline points="7 10 12 15 17 10" />
                              <line x1="12" y1="15" x2="12" y2="3" />
                            </svg>
                            Download PDF
                          </a>
                        ) : null}
                      </div>
                    </div>

                    {/* Share */}
                    <div className="wph__share">
                      <span className="wph__share-label">Share</span>
                      <div className="wph__share-icons">
                        <div className="wph__share-icon wph__share-icon--linkedin">
                          <LinkedinShareButton url={articleUrl}>
                            <FaLinkedinIn color="#fff" size="13" />
                          </LinkedinShareButton>
                        </div>
                        <div className="wph__share-icon wph__share-icon--facebook">
                          <FacebookShareButton url={articleUrl}>
                            <FaFacebookF color="#fff" size="13" />
                          </FacebookShareButton>
                        </div>
                        <div className="wph__share-icon wph__share-icon--twitter">
                          <TwitterShareButton url={articleUrl}>
                            <FaTwitter color="#fff" size="13" />
                          </TwitterShareButton>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          )}

          {/* ============ 2. BODY ============ */}
          {hasBody && (
            <section className="wpb">
              <div className="wpb__inner body-container">
                {whitePaperContent?.primary_description_1 && (
                  <div
                    className="wpb__desc"
                    dangerouslySetInnerHTML={{
                      __html: whitePaperContent.primary_description_1,
                    }}
                  />
                )}

                {whitePaperContent?.primary_description_2 && (
                  <div
                    className="wpb__desc"
                    dangerouslySetInnerHTML={{
                      __html: whitePaperContent.primary_description_2,
                    }}
                  />
                )}

                {whitePaperContent?.primary_description_3 && (
                  <div
                    className="wpb__desc"
                    dangerouslySetInnerHTML={{
                      __html: whitePaperContent.primary_description_3,
                    }}
                  />
                )}

                {/* Key points */}
                {keyPoints.length > 0 && (
                  <div className="wpb__kp">
                    {whitePaperContent?.secondary_title && (
                      <h2 className="wpb__kp-heading">
                        {whitePaperContent.secondary_title}
                      </h2>
                    )}
                    <div className="wpb__kp-grid">
                      {keyPoints.map((kp, i) => (
                        <div key={i} className="wpb__kp-card">
                          <p className="wpb__kp-card-title">{kp.title}</p>
                          {kp.desc && (
                            <p className="wpb__kp-card-desc">{kp.desc}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Video */}
                {whitePaperContent?.video_url && (
                  <div className="wpb__video">
                    <video autoPlay playsInline loop muted>
                      <source
                        src={whitePaperContent.video_url}
                        type="video/mp4"
                      />
                    </video>
                    {whitePaperContent?.video_description && (
                      <p className="wpb__video-caption">
                        {whitePaperContent.video_description}
                      </p>
                    )}
                  </div>
                )}

                {/* Testimonials */}
                {whitePaperContent?.testimonial?.length > 0 && (
                  <div className="wpb__testimonials">
                    {whitePaperContent.testimonial.map((t, i) => (
                      <div key={i} className="wpb__testimonial">
                        <p className="wpb__testimonial-text">{t.content}</p>
                        <span className="wpb__testimonial-client">
                          &mdash; {t.client_name}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </section>
          )}

          {/* ============ 3. ACCESS SECTION ============ */}
          {(hasForm || hasPdf) && (
            <section className="wpa" ref={formRef} id="wp-access">
              <div className="body-container">
                {hasForm ? (
                  <div className="wpa__split">
                    <div className="wpa__text">
                      <span className="wpa__eyebrow">Get the Full Report</span>
                      <h2 className="wpa__heading">
                        {whitePaperContent?.banner_image_title ||
                          whitePaperContent?.title}
                      </h2>
                      <p className="wpa__sub">
                        Fill in your details below to instantly access the
                        complete white paper, free of charge.
                      </p>
                    </div>
                    <div className="wpa__form-wrapper">
                      <ContactUsRightGrid
                        bannerTitle={whitePaperContent.banner_image_title}
                        pdfFile={whitePaperContent.pdf_file}
                        articleid={whitePaperContent.id}
                        type="white-paper"
                      />
                    </div>
                  </div>
                ) : (
                  <div className="wpa__download">
                    <div className="wpa__download-icon">
                      <svg
                        width="28"
                        height="28"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                        <polyline points="14 2 14 8 20 8" />
                        <line x1="16" y1="13" x2="8" y2="13" />
                        <line x1="16" y1="17" x2="8" y2="17" />
                      </svg>
                    </div>
                    <h2 className="wpa__download-heading">
                      Ready to Read the Full Report?
                    </h2>
                    <p className="wpa__download-sub">
                      Download the complete white paper to explore our in-depth
                      research and strategic insights.
                    </p>
                    <a
                      href={whitePaperContent.pdf_file}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="wpa__download-btn"
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                        <polyline points="7 10 12 15 17 10" />
                        <line x1="12" y1="15" x2="12" y2="3" />
                      </svg>
                      Download Free PDF
                    </a>
                  </div>
                )}
              </div>
            </section>
          )}

          {/* ============ 4. CTA STRIP ============ */}
          <div className="wpc">
            <div className="wpc__inner body-container">
              <div className="wpc__text">
                <h2>Ready to accelerate your technology journey?</h2>
                <p>
                  Talk to our experts about how digiRyte can deliver enterprise
                  outcomes at AI-native speed.
                </p>
              </div>
              <a href="/contact-us" className="wpc__btn">
                Get in Touch
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M3 8h10M9 4l4 4-4 4"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </a>
            </div>
          </div>
        </>
      )}
    </Layout>
  );
};

export default WhitePaper;
